/**
 * Pure state machine + status mapping for the passwordless (email OTP) flow.
 *
 * Kept free of SuperTokens imports so the whole flow — including every error
 * branch — is unit-testable. The network wrappers live in ./client.ts and only
 * feed statuses in here.
 */
import {localize} from "@agenta/i18n"

export type OtpPhase = "email" | "sending" | "code" | "verifying"

export interface OtpState {
    phase: OtpPhase
    email: string
    code: string
    /** Blocking failure shown above the field. */
    error: string | null
    /** Transient confirmation ("New code sent"). */
    notice: string | null
    /** True during the 60s post-resend cooldown. */
    resendBlocked: boolean
}

export const initialOtpState = (email = ""): OtpState => ({
    phase: "email",
    email,
    code: "",
    error: null,
    notice: null,
    resendBlocked: false,
})

export type OtpEvent =
    | {type: "edit-email"; value: string}
    | {type: "edit-code"; value: string}
    | {type: "send"}
    | {type: "sent"}
    | {type: "resent"}
    | {type: "resend-allowed"}
    | {type: "verify"}
    | {type: "failed"; message: string}
    | {type: "restart"; message?: string}

export function otpReducer(state: OtpState, event: OtpEvent): OtpState {
    switch (event.type) {
        case "edit-email":
            return {...state, email: event.value, error: null}
        case "edit-code":
            return {...state, code: event.value, error: null}
        case "send":
            return {...state, phase: "sending", error: null, notice: null}
        case "sent":
            return {...state, phase: "code", code: "", error: null, notice: null}
        case "resent":
            return {
                ...state,
                notice: localize("com_auth_new_code_sent"),
                error: null,
                resendBlocked: true,
            }
        case "resend-allowed":
            return {...state, resendBlocked: false}
        case "verify":
            return {...state, phase: "verifying", error: null, notice: null}
        case "failed":
            // Stay on whichever step the user was on; only the spinner ends.
            return {
                ...state,
                phase: state.phase === "sending" ? "email" : "code",
                error: event.message,
                notice: null,
            }
        case "restart":
            return {
                ...initialOtpState(state.email),
                error: event.message ?? null,
            }
        default:
            return state
    }
}

/** 60s, matching the desktop resend cooldown. */
export const OTP_RESEND_COOLDOWN_MS = 60_000

/** A key, not copy: translated when the message is built, so it follows the language. */
const GENERIC_ERROR = "com_auth_otp_generic_error"

export function describeCreateCode(
    result: {status: string; reason?: string} | null,
): {kind: "ok"} | {kind: "failed"; message: string} {
    if (result?.status === "OK") return {kind: "ok"}
    if (result?.status === "SIGN_IN_UP_NOT_ALLOWED") {
        return {
            kind: "failed",
            message: result.reason || localize("com_auth_not_allowed_for_email"),
        }
    }
    return {kind: "failed", message: localize(GENERIC_ERROR)}
}

export function describeResendCode(
    result: {status: string} | null,
): {kind: "ok"} | {kind: "restart"; message: string} {
    if (result?.status === "OK") return {kind: "ok"}
    if (result?.status === "RESTART_FLOW_ERROR") {
        return {kind: "restart", message: localize("com_auth_code_expired_restart")}
    }
    return {kind: "restart", message: localize(GENERIC_ERROR)}
}
